<template>
  <div>
    <button class="d-none" @click="sendEmail()">Send test email</button>
    <CSidebar
      :visible="visible"
      :class="{ show: visible == true, hide: visible == false }"
      position="fixed"
      class="sidebar--width"
      @visible-change="store.dispatch('setMenuVisible', $event)"
    >
      <CSidebarBrand>
        <div class="country-flag" :title="countryCode">
          <span class="country-flag__emoji">{{ countryFlagEmoji }}</span>
        </div>
        <CCloseButton white class="close mx-2" @click="store.dispatch('setMenuVisible', false)" />
      </CSidebarBrand>
      <CSidebarNav>
        <template v-for="(link, id) in links" :key="`parent_${id}`">
          <MenuItems v-if="canRenderLink(link)" :link="link"></MenuItems>
        </template>
      </CSidebarNav>
    </CSidebar>
    <div class="wrapper d-flex flex-column min-vh-100 bg-light">
      <AppHeader />
      <div class="body flex-grow-1 px-3">
        <CContainer lg>
          <router-view name="content" />
        </CContainer>
      </div>
      <AppFooter />
    </div>
  </div>
</template>
<script setup>
import { CContainer, CSidebar } from '@coreui/vue'
import AppFooter from '@/components/AppFooter.vue'
import AppHeader from '@/components/AppHeader.vue'
import { useStore } from 'vuex'
import { ref, computed, watch, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import MenuItems from '@/components/MenuItems.vue'
import { isInLegacyGroup, isModuleEnabled } from '@/helpers/tenantModules'
import { StatService } from '@/services/stat.service'
import TokenService from '@/services/storage.service'
import { isAuthSessionExpired } from '@/auth/session'

const store = useStore()
const router = useRouter()
const currentCountry = ref((localStorage.getItem('currentCountry') || '').toUpperCase())
const countryCode = computed(() => {
  const code = (currentCountry.value || '').trim().toUpperCase()
  if (!code) return 'UN'
  if (code === 'UK') return 'GB'
  return code
})
const countryFlagEmoji = computed(() => {
  if (countryCode.value === 'HQ') return '🌐'
  if (!/^[A-Z]{2}$/.test(countryCode.value)) return '🏳️'
  const first = countryCode.value.codePointAt(0) + 127397
  const second = countryCode.value.codePointAt(1) + 127397
  return String.fromCodePoint(first, second)
})

const user = computed(() => store.getters.getAdminMe)
const links = computed(() => router.getRoutes().filter((item) => item.meta.isParent === true))
const isLoadingUser = ref(true)
const isLoadingModules = computed(() => {
  const tenancyEnabled = store.getters.tenancyEnabled
  const modulesBySlug = store.getters.modulesBySlug
  const availableModules = store.getters.availableModules

  if (tenancyEnabled) {
    const hasModulesBySlug = modulesBySlug && Object.keys(modulesBySlug).length > 0
    const hasAvailableModules = availableModules && availableModules.length > 0

    if (!hasModulesBySlug && !hasAvailableModules) {
      console.log('⏳ Modules still loading:', {
        tenancyEnabled,
        hasModulesBySlug,
        hasAvailableModules,
        modulesBySlugKeys: modulesBySlug ? Object.keys(modulesBySlug).length : 0,
        availableModulesCount: availableModules ? availableModules.length : 0
      })
      return true
    }
  }

  console.log('✅ Modules loaded:', {
    tenancyEnabled,
    hasModulesBySlug: modulesBySlug && Object.keys(modulesBySlug).length > 0,
    hasAvailableModules: availableModules && availableModules.length > 0
  })
  return false // Modules are loaded (or tenancy is disabled)
})

if (TokenService.getToken() && !isAuthSessionExpired()) {
  store.dispatch('me').then(async () => {
    if (!TokenService.getToken() || isAuthSessionExpired()) {
      return
    }

    isLoadingUser.value = false
    store.dispatch('hydrateRequestStatsFromSession')
    await store.dispatch('loadRequestStats', { useCache: true })

    const tenantModules = store.getters.tenantModules
    const availableModules = store.getters.availableModules

    if (Object.keys(tenantModules).length === 0 || availableModules.length === 0) {
      try {
        await store.dispatch('loadPermissions')
        await store.dispatch('loadTenantModules')
      } catch (error) {
        console.error('[DefaultLayout] Failed to load permissions/modules:', error)
      }
    }
  })
}

function getModuleSlugFromRoute(link) {
  if (link.meta?.moduleSlug) {
    return link.meta.moduleSlug
  }

  const routeName = link.name || ''
  const modulesBySlug = store.getters.modulesBySlug || {}
  const availableModules = store.getters.availableModules || []

  if (modulesBySlug[routeName]) {
    return routeName
  }

  for (const slug of availableModules) {
    if (routeName.startsWith(slug) || routeName.includes(slug + '_') || routeName.includes(slug + '-')) {
      return slug
    }
  }

  return routeName
}

function hasPermission(link) {
  if (isLoadingUser.value) {
    console.log(`⏳ ${link.name}: Waiting for user data`)
    return false
  }

  if (store.getters.tenancyEnabled && isLoadingModules.value) {
    console.log(`⏳ ${link.name}: Waiting for modules to load (isLoadingModules=${isLoadingModules.value})`)
    return false
  }

  const userRoles = user.value?.roles || []

  if (!user.value) {
    console.warn(`⚠️ ${link.name}: user.value is null/undefined`)
  } else if (!userRoles || userRoles.length === 0) {
    console.warn(`⚠️ ${link.name}: user.value.roles is empty:`, user.value)
  }

  // Check if the user's role matches the required role for the route
  const hasRole =
    link.meta.requiredRole && link.meta.requiredRole.length > 0
      ? userRoles.some((item) => {
          const roleName = item?.name || item
          return link.meta.requiredRole.includes(roleName)
        })
      : true

  // Check if the current tenant matches any allowed tenant groups for the route
  const allowedTenantGroups = link.meta.allowedTenant || []
  const moduleSlug = getModuleSlugFromRoute(link)
  const checkModuleConfig = link.meta.checkModuleConfig
  let isTenantAllowed = false

  if (allowedTenantGroups.length > 0) {
    // Use legacy group checking if allowedTenant is specified
    isTenantAllowed = allowedTenantGroups.some((group) => {
      return isInLegacyGroup(group)
    })
  } else if (checkModuleConfig && moduleSlug) {
    // First check if the module is enabled, then check if the config is enabled
    const moduleEnabled = isModuleEnabled(moduleSlug)
    if (moduleEnabled) {
      const configValue = store.getters.getModuleConfig(moduleSlug, checkModuleConfig, null)
      isTenantAllowed = configValue === true
    } else {
      isTenantAllowed = false
    }
  } else {
    // If no allowedTenant specified, check if module is enabled based on route name
    isTenantAllowed = isModuleEnabled(moduleSlug)
  }

  if (isTenantAllowed && moduleSlug) {
    const userPermissions = store.getters.userPermissions || []
    const moduleData = store.getters.modulesBySlug[moduleSlug]
    if (moduleData && userPermissions.length > 0) {
      const hasUserPermission = userPermissions.includes(moduleData.id)
      if (!hasUserPermission) {
        isTenantAllowed = false
      }
    }
  }

  const modulesBySlug = store.getters.modulesBySlug
  const availableModules = store.getters.availableModules
  const userPermissions = store.getters.userPermissions
  const isModuleEnabledResult = isModuleEnabled(moduleSlug)
  const result = hasRole && isTenantAllowed

  const moduleExists = !!modulesBySlug?.[moduleSlug]
  const moduleInAvailable = availableModules?.includes(moduleSlug)
  const allModuleSlugs = Object.keys(modulesBySlug || {})

  let moduleConfigValue = null
  if (checkModuleConfig && moduleSlug) {
    moduleConfigValue = store.getters.getModuleConfig(moduleSlug, checkModuleConfig, null)
    console.log(`🔍 Config check for ${link.name}:`, {
      moduleSlug,
      checkModuleConfig,
      configValue: moduleConfigValue,
      configType: typeof moduleConfigValue,
      configStrictEqual: moduleConfigValue === true,
      isTenantAllowed
    })
  }

  const moduleData = modulesBySlug[moduleSlug]
  const userHasModulePermission = moduleData && userPermissions.length > 0 ? userPermissions.includes(moduleData.id) : true

  console.log(`${result ? '✅' : '❌'} ${link.name}:`, {
    hasRole,
    tenantAllowed: isTenantAllowed,
    moduleSlug,
    moduleId: moduleData?.id,
    userHasModulePermission,
    userPermissionsCount: userPermissions.length,
    checkModuleConfig,
    moduleConfigValue,
    moduleExists,
    moduleInAvailable,
    isModuleEnabled: isModuleEnabledResult,
    isLoadingModules: isLoadingModules.value,
    availableModulesCount: availableModules?.length || 0,
    modulesBySlugCount: allModuleSlugs.length
  })

  return result
}

function hasAnyChildPermission(link) {
  if (!Array.isArray(link.children) || link.children.length === 0) {
    return false
  }

  return link.children.some((child) => {
    if (Array.isArray(child.children) && child.children.length > 0) {
      return hasAnyChildPermission(child)
    }

    return hasPermission(child)
  })
}

function canRenderLink(link) {
  if (Array.isArray(link.children) && link.children.length > 0) {
    return hasAnyChildPermission(link)
  }

  return hasPermission(link)
}

const visible = computed(() => store.getters.getMenuVisible)

function showSidebar() {
  const width = window.innerWidth
  store.dispatch('setMenuVisible', width > 800)
}

async function sendEmail() {
  await StatService.testEmail()
}

watch(() => window.innerWidth, showSidebar)
watch(
  () => router.currentRoute.value.path,
  (path) => {
    const usesRequestStats =
      path.startsWith('/requests') ||
      path.startsWith('/users-roles') ||
      path.startsWith('/products-categories') ||
      path.startsWith('/orders') ||
      path === '/dashboard'

    if (usesRequestStats) {
      store.dispatch('loadRequestStats', { useCache: true })
    }
  }
)
window.addEventListener('resize', showSidebar)

onUnmounted(() => {
  window.removeEventListener('resize', showSidebar)
})
</script>

<style src="@/styles/DefaultLayout.scss" lang="scss" scoped></style>
